import { fetchStorageObjectBlob, usesStorageEmulator } from '@/firebase/storage-routing'
import { useEffect, useState, useCallback, useMemo } from 'react'
import * as Sentry from '@sentry/react'

type UseGcsPdfLoaderOptions = {
  filePath: string
  gcsBucket: string
  legacy_always_cloud?: boolean
  sentryContext?: Record<string, unknown>
}

type UseGcsPdfLoaderReturn = {
  loading: boolean
  error: boolean
  pdfFile: File | null
  retry: () => void
}

/**
 * Hook for loading PDF files from Google Cloud Storage
 *
 * Handles fetching, validation, error handling, and retry logic.
 * Centralizes common PDF loading logic across different viewer contexts.
 *
 * Features:
 * - Fetches PDF blob from GCS
 * - Validates MIME type (must be application/pdf)
 * - Provides loading and error states
 * - Supports retry functionality
 * - Logs errors to Sentry with context
 *
 * @param options - Configuration for PDF loading
 * @param options.filePath - GCS file path to fetch
 * @param options.gcsBucket - GCS bucket reference
 * @param options.sentryContext - Additional context for Sentry logging
 * @returns Loading state, error state, PDF file, and retry function
 *
 * @example
 * ```typescript
 * const { loading, error, pdfFile, retry } = useGcsPdfLoader({
 *   filePath: 'documents/report.pdf',
 *   gcsBucket: kGcsUserFileUploadsBucketRef,
 *   sentryContext: { title: 'Report', userId: '123' }
 * })
 *
 * if (loading) return <Spinner />
 * if (error) return <ErrorWithRetry onRetry={retry} />
 * return <PDFViewer file={pdfFile} />
 * ```
 */
export function useGcsPdfLoader(options: UseGcsPdfLoaderOptions): UseGcsPdfLoaderReturn {
  const { filePath, gcsBucket, legacy_always_cloud = true, sentryContext = {} } = options

  // Initialize loading as true since we start loading immediately in the effect (prevents flashing when using the loading state)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [refetchCount, setRefetchCount] = useState(0)

  // Stringify sentryContext to avoid object reference issues in dependencies
  const sentryContextString = useMemo(() => JSON.stringify(sentryContext), [sentryContext])
  const sentryContextSafe = useMemo<Record<string, unknown>>(() => {
    try {
      return JSON.parse(sentryContextString) as Record<string, unknown>
    } catch {
      return {}
    }
  }, [sentryContextString])

  useEffect(() => {
    let cancelled = false

    async function fetchPdf() {
      // Reset state at the start of fetch
      setLoading(true)
      setError(false)
      setPdfFile(null)

      try {
        const blob: Blob = await fetchStorageObjectBlob(gcsBucket, filePath, { legacy_always_cloud })

        if (cancelled) return

        const blobType = blob.type

        // Local-only allowance: the Storage emulator serves application/octet-stream
        // regardless of the stored contentType. Real GCS returns application/pdf.
        const allowEmulatorOctetStream = usesStorageEmulator({ legacy_always_cloud }) && blobType === 'application/octet-stream'
        if (blobType !== 'application/pdf' && !allowEmulatorOctetStream) {
          Sentry.captureException(new Error('PDF Reference Blob is not application/pdf'), {
            extra: { ...sentryContextSafe, blobType, filePath },
          })
          setError(true)
          return
        }

        setPdfFile(blob as File)
      } catch (e) {
        if (cancelled) return

        console.error('Error loading PDF from GCS:', e)
        Sentry.captureException(e, { extra: { ...sentryContextSafe, filePath } })
        setError(true)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchPdf()

    return () => {
      cancelled = true
    }
  }, [filePath, gcsBucket, legacy_always_cloud, sentryContextSafe, refetchCount])

  const retry = useCallback(() => {
    setRefetchCount((prev) => prev + 1)
  }, [])

  return { loading, error, pdfFile, retry }
}
